import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CreatedDocument, ReceiptWithAllocationsResult, InvoiceCloseResult } from './types';
import { retryCloseInvoices, getCustomerDebtReport } from '../../api/client';
import OpenBalanceWhatsAppModal from '../../components/OpenBalanceWhatsAppModal';

interface SuccessStepProps {
  document: CreatedDocument;
  allocationsResult?: ReceiptWithAllocationsResult | null;
  customerId?: number | null;
  customerName?: string;
  customerMessagesEnabled?: boolean;
  onReset: () => void;
}

export default function SuccessStep({ document, allocationsResult, customerId, customerName, customerMessagesEnabled, onReset }: SuccessStepProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<ReceiptWithAllocationsResult | null>(null);
  const [agedDebt, setAgedDebt] = useState<{ amount: number; unpaid: number; days: number } | null>(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    getCustomerDebtReport(customerId)
      .then((res) => {
        if (!active) return;
        const data = res.data;
        if (data && data.aged_debt_amount > 0) {
          setAgedDebt({
            amount: data.aged_debt_amount,
            unpaid: data.unpaid_amount || 0,
            days: data.payment_terms_days || 60,
          });
        }
      })
      .catch(() => {
        // Non-critical; skip debt alert on failure
      });
    return () => { active = false; };
  }, [customerId]);

  const result = retryResult || allocationsResult;
  const hasAllocations = result && ((result.invoice_allocations?.length ?? 0) > 0 || (result.failed_invoice_allocations?.length ?? 0) > 0);
  const hasDeliveryNoteClosures = (document.delivery_note_closures?.length ?? 0) > 0 || (document.failed_delivery_note_closures?.length ?? 0) > 0;
  const hasFailedDeliveryNoteClosures = (document.failed_delivery_note_closures?.length ?? 0) > 0;
  const hasFailed = (result && (result.failed_invoice_allocations?.length ?? 0) > 0) || hasFailedDeliveryNoteClosures;

  const handleRetry = useCallback(async () => {
    if (!result || !hasFailed) return;
    setRetrying(true);
    try {
      const res = await retryCloseInvoices({
        receipt_document_type: document.document_type,
        receipt_document_number: document.document_number,
        invoices_to_close: (result.failed_invoice_allocations ?? []).map((f: InvoiceCloseResult) => ({
          document_type: f.document_type,
          document_number: f.document_number,
          amount_close: f.amount_close,
          open_balance: f.open_balance_before,
        })),
      });
      setRetryResult(res.data as ReceiptWithAllocationsResult);
    } catch {
      // Keep existing result on retry failure
    } finally {
      setRetrying(false);
    }
  }, [result, hasFailed, document]);

  return (
    <div className="flex flex-col items-center max-w-lg mx-auto py-12 animate-scale-in">
      {/* Green/Yellow checkmark based on status */}
      <div className="mb-6">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
          hasFailed ? 'bg-yellow-100' : 'bg-green-100'
        }`}>
          <svg
            className={`w-14 h-14 ${hasFailed ? 'text-yellow-500' : 'text-green-500'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
        {t('createDocument.success.title')}
      </h2>

      {/* Partial success warning */}
      {hasFailed && (
        <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-4 text-center">
          {t('createDocument.success.partialSuccess')}
        </p>
      )}

      {/* Document number */}
      <p className="text-gray-500 mb-1 text-center">
        {t('createDocument.success.documentNumber')}
      </p>
      <p className="text-2xl font-bold text-gray-900 mb-6">
        #{document.document_number}
      </p>

      {/* Aged debt alert */}
      {agedDebt && (
        <div className="w-full mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                {t('createDocument.success.debtAlertTitle', 'Customer has overdue debt')}
              </p>
              <p className="text-sm text-red-700 mt-1">
                {t('createDocument.success.debtAlertMessage',
                  '{{name}} has ₪{{amount}} in debt over {{days}} days old.',
                  { name: customerName || `#${customerId}`, amount: agedDebt.amount.toLocaleString(), days: agedDebt.days })}
              </p>
              <button
                type="button"
                onClick={() => setShowWhatsAppModal(true)}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.264-1.227l-.306-.183-2.87.852.852-2.87-.183-.306A8 8 0 1112 20z" />
                </svg>
                {t('createDocument.success.sendDebtWhatsApp', 'Send debt notice via WhatsApp')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Allocation Results */}
      {hasAllocations && (
        <div className="w-full mb-6 rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              {t('createDocument.success.invoicesClosed')}
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs">
                <th className="px-4 py-2 text-start font-medium">{t('createDocument.invoices.invoiceNumber')}</th>
                <th className="px-4 py-2 text-end font-medium">{t('createDocument.invoices.amountToClose')}</th>
                <th className="px-4 py-2 text-start font-medium">{t('createDocument.invoices.status')}</th>
                <th className="px-4 py-2 text-start font-medium">Rivhit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(result!.invoice_allocations ?? []).map((alloc) => (
                <tr key={`${alloc.document_type}-${alloc.document_number}`} className="bg-white">
                  <td className="px-4 py-2 font-medium text-gray-800">{alloc.document_number}</td>
                  <td className="px-4 py-2 text-end text-gray-600">₪{Number(alloc.amount_close).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs font-semibold ${
                      alloc.close_status === 'fully_closed' ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {alloc.close_status === 'fully_closed'
                        ? t('createDocument.success.fullyClosed')
                        : alloc.close_status === 'partially_closed'
                          ? t('createDocument.success.partiallyClosed')
                          : t('createDocument.success.closedByAmount')}
                    </span>
                    {alloc.remaining_balance != null && Number(alloc.remaining_balance) > 0 && (
                      <span className="block text-xs text-gray-400">
                        {t('createDocument.success.remainingBalance', { amount: Number(alloc.remaining_balance).toFixed(2) })}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium text-green-600">
                      ✓ {t('createDocument.success.closeSuccess')}
                    </span>
                  </td>
                </tr>
              ))}
              {(result!.failed_invoice_allocations ?? []).map((alloc) => (
                <tr key={`fail-${alloc.document_type}-${alloc.document_number}`} className="bg-red-50/50">
                  <td className="px-4 py-2 font-medium text-gray-800">{alloc.document_number}</td>
                  <td className="px-4 py-2 text-end text-gray-600">₪{Number(alloc.amount_close).toFixed(2)}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">—</td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium text-red-600">
                      ✗ {t('createDocument.success.closeFailed')}
                    </span>
                    {alloc.error && (
                      <span className="block text-xs text-red-400 truncate max-w-[150px]" title={alloc.error}>
                        {alloc.error}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasDeliveryNoteClosures && (
        <div className="w-full mb-6 rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              {t('createDocument.success.deliveryNotesClosed')}
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs">
                <th className="px-4 py-2 text-start font-medium">{t('createDocument.deliveryNotes.number')}</th>
                <th className="px-4 py-2 text-end font-medium">{t('createDocument.deliveryNotes.amount')}</th>
                <th className="px-4 py-2 text-start font-medium">Rivhit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(document.delivery_note_closures ?? []).map((closure) => (
                <tr key={`${closure.document_type}-${closure.document_number}`} className="bg-white">
                  <td className="px-4 py-2 font-medium text-gray-800">{closure.document_number}</td>
                  <td className="px-4 py-2 text-end text-gray-600">₪{Number(closure.amount_close).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium text-green-600">
                      ✓ {t('createDocument.success.closeSuccess')}
                    </span>
                  </td>
                </tr>
              ))}
              {(document.failed_delivery_note_closures ?? []).map((closure) => (
                <tr key={`fail-delivery-${closure.document_type}-${closure.document_number}`} className="bg-red-50/50">
                  <td className="px-4 py-2 font-medium text-gray-800">{closure.document_number}</td>
                  <td className="px-4 py-2 text-end text-gray-600">₪{Number(closure.amount_close).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium text-red-600">
                      ✗ {t('createDocument.success.closeFailed')}
                    </span>
                    {closure.error && (
                      <span className="block text-xs text-red-400 truncate max-w-[150px]" title={closure.error}>
                        {closure.error}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Buttons */}
      <div className="flex flex-col gap-4 w-full">
        {/* Retry failed closures */}
        {hasFailed && (
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className="w-full py-3 bg-yellow-500 text-white font-semibold rounded-xl hover:bg-yellow-600 transition-colors disabled:opacity-60"
          >
            {retrying ? t('createDocument.success.retrying') : t('createDocument.success.retryFailed')}
          </button>
        )}

        {document.document_link && (
          <button
            type="button"
            onClick={() => window.open(document.document_link, '_blank')}
            className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
          >
            {t('createDocument.success.openPdf')}
            <svg
              className="w-4 h-4 rtl:rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
        )}

        <button
          type="button"
          onClick={onReset}
          className="w-full py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          {t('createDocument.success.createAnother')}
        </button>

        <button
          type="button"
          onClick={() => navigate('/documents')}
          className="text-gray-500 underline hover:text-gray-700 transition-colors text-sm"
        >
          {t('createDocument.success.backToDocuments')}
        </button>
      </div>

      {showWhatsAppModal && customerId && (
        <OpenBalanceWhatsAppModal
          customerId={customerId}
          displayName={customerName}
          messagesEnabled={customerMessagesEnabled}
          onClose={() => setShowWhatsAppModal(false)}
        />
      )}
    </div>
  );
}
